import React, { useState, useEffect } from 'react';
import { BrandButton, TextCancelButton } from '../button';
import {
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Paper
} from '@mui/material';
import axios from 'axios';
import AccountStatusIcon from '../AccountStatusIcon';
import SmtpServerBasicsFields from '../email/SmtpServerBasicsFields';
import {
  MenuItem,
  TableActionsMenu,
  TableActionsMenuButton,
  destructiveMenuItemSx,
  stopTableRowClick,
} from '../ui';

function SmtpTab() {
  const [accounts, setAccounts] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [formData, setFormData] = useState({
    email: '',
    accountName: '',
    incomingServer: '',
    incomingPort: 993,
    incomingConnection: 'SSL/TLS',
    incomingPassword: '',
    outgoingServer: '',
    outgoingPort: 465,
    outgoingConnection: 'SSL/TLS',
    outgoingPassword: '',
    status: 'Active'
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const res = await axios.get('/api/mail/accounts');
      setAccounts(res.data);
    } catch (error) {
      console.error('Error fetching SMTP accounts:', error);
    }
  };

  const handleOpenMenu = (event, account) => {
    setAnchorEl(event.currentTarget);
    setSelectedAccount(account);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedAccount(null);
  };

  const defaultForm = () => ({
    email: '',
    accountName: '',
    incomingServer: '',
    incomingPort: 993,
    incomingConnection: 'SSL/TLS',
    incomingPassword: '',
    outgoingServer: '',
    outgoingPort: 465,
    outgoingConnection: 'SSL/TLS',
    outgoingPassword: '',
    status: 'Active'
  });

  const handleAddEmail = () => {
    setDialogMode('add');
    setFormData(defaultForm());
    setOpenDialog(true);
  };

  const handleChangeEmail = () => {
    if (!selectedAccount) return;
    setDialogMode('edit');
    setFormData({
      email: selectedAccount.email,
      accountName: selectedAccount.accountName || '',
      incomingServer: selectedAccount.incomingServer,
      incomingPort: selectedAccount.incomingPort,
      incomingConnection: selectedAccount.incomingConnection,
      incomingPassword: selectedAccount.incomingPassword,
      outgoingServer: selectedAccount.outgoingServer,
      outgoingPort: selectedAccount.outgoingPort,
      outgoingConnection: selectedAccount.outgoingConnection,
      outgoingPassword: selectedAccount.outgoingPassword,
      status: selectedAccount.status
    });
    setOpenDialog(true);
  };

  const handleDeleteEmail = async () => {
    if (!selectedAccount) return;
    const confirmDelete = window.confirm('Do you really want to delete this email?');
    if (!confirmDelete) return;
    try {
      await axios.delete(`/api/mail/accounts/${selectedAccount._id}`);
      handleCloseMenu();
      fetchAccounts();
    } catch (error) {
      console.error('Error deleting email account:', error);
    }
  };

  const handleSaveEmail = async () => {
    try {
      if (dialogMode === 'add') {
        await axios.post('/api/mail/accounts', formData);
      } else {
        await axios.put(`/api/mail/accounts/${selectedAccount._id}`, formData);
      }
      setOpenDialog(false);
      fetchAccounts();
    } catch (error) {
      console.error('Error saving email account:', error);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleChangeField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <Box sx={{ backgroundColor: '#F3F7FA', p: 2, height: 'calc(100vh - 100px)' }}>
      <Paper sx={{ p: 3, mb: 3, boxShadow: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Email Accounts</Typography>
          <BrandButton onClick={handleAddEmail}>
            Add email
          </BrandButton>
        </Box>

        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Account Name</TableCell>
              <TableCell>Connection protection</TableCell>
              <TableCell>Port</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {accounts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No email accounts added yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              accounts.map((acc) => (
                <TableRow key={acc._id}>
                  <TableCell>{acc.email}</TableCell>
                  <TableCell>{acc.accountName || '-'}</TableCell>
                  <TableCell>{acc.incomingConnection}</TableCell>
                  <TableCell>{acc.incomingPort}</TableCell>
                  <TableCell>
                    <AccountStatusIcon status={acc.status} validationErrors={acc.validationErrors} />
                  </TableCell>
                  <TableCell onClick={stopTableRowClick}>
                    <TableActionsMenuButton
                      ariaLabel="Email account actions"
                      onClick={(e) => handleOpenMenu(e, acc)}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      <TableActionsMenu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
        <MenuItem onClick={handleChangeEmail}>Change email</MenuItem>
        <MenuItem onClick={handleDeleteEmail} sx={destructiveMenuItemSx}>Delete</MenuItem>
      </TableActionsMenu>

      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {dialogMode === 'add' ? 'Add email' : 'Change email'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1">Incoming mail server</Typography>
            <SmtpServerBasicsFields
              formData={formData}
              handleChangeField={handleChangeField}
              serverLabel="Server name imap"
              serverField="incomingServer"
              portField="incomingPort"
              connectionField="incomingConnection"
            />
            <TextField
              margin="dense"
              label="Email"
              fullWidth
              value={formData.email}
              onChange={(e) => handleChangeField('email', e.target.value)}
            />
            <TextField
              margin="dense"
              label="Account Name (Sender Name)"
              fullWidth
              value={formData.accountName}
              onChange={(e) => handleChangeField('accountName', e.target.value)}
              placeholder="e.g., John Smith, Company Support"
            />
            <TextField
              margin="dense"
              label="Password"
              type="password"
              fullWidth
              value={formData.incomingPassword}
              onChange={(e) => handleChangeField('incomingPassword', e.target.value)}
              inputProps={{
                autocomplete: 'current-password'
              }}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1">Outgoing mail server</Typography>
            <SmtpServerBasicsFields
              formData={formData}
              handleChangeField={handleChangeField}
              serverLabel="Server name smtp"
              serverField="outgoingServer"
              portField="outgoingPort"
              connectionField="outgoingConnection"
            />
            <TextField
              margin="dense"
              label="Email (same or different?)"
              fullWidth
              value={formData.email}
              onChange={(e) => handleChangeField('email', e.target.value)}
            />
            <TextField
              margin="dense"
              label="Password"
              type="password"
              fullWidth
              value={formData.outgoingPassword}
              onChange={(e) => handleChangeField('outgoingPassword', e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <TextCancelButton onClick={handleCloseDialog}>Cancel</TextCancelButton>
          <BrandButton onClick={handleSaveEmail}>
            {dialogMode === 'add' ? 'Add email' : 'Save changes'}
          </BrandButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SmtpTab;
